<template>
  <div>
    <div class="loader" v-if="isLoading"></div>
    <div class="loadertext" v-if="isLoading">
      {{ $t("loadingTxt") }}...
    </div>
    <div class="main-wrapper" :class="{ 'blur-content': isLoading }">
      <sidebar-component classMenu="TemplateSetting"></sidebar-component>

      <div class="page-wrapper">
        <navbar-component></navbar-component>

        <div class="page-content">
          <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
            <div>
              <h4 class="mb-1">PI NON STD - Proforma Rental</h4>
            </div>
            <div v-if="permision_role.method_create">
              <button class="btn btn-success btn-sm" @click="openCreateModal">
                <i class="mdi mdi-plus-circle-outline me-1"></i> Tambah
              </button>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <vue-good-table
                mode="remote"
                :columns="columns"
                :rows="rows"
                :totalRows="totalRecords"
                :isLoading.sync="isLoading"
                :pagination-options="{
                  enabled: true,
                  perPage: serverParams.per_page,
                  perPageDropdown: [],
                }"
                styleClass="vgt-table striped bordered"
                @on-page-change="onPageChange"
              >
                <!-- Filter row per kolom -->
                <template slot="column-filter" slot-scope="props">
                  <input
                    v-if="props.column.field !== 'actions' && props.column.field !== 'rental_prorate'"
                    type="text"
                    class="vgt-input"
                    :placeholder="props.column.label"
                    v-model="columnFilters[props.column.field]"
                    @input="onColumnFilter"
                  />
                </template>

                <template slot="table-row" slot-scope="props">
                  <!-- Kolom Action -->
                  <span v-if="props.column.field === 'actions'">
                    <div class="d-flex gap-1">
                      <button
                        v-if="permision_role.method_read"
                        type="button"
                        class="btn btn-icon btn-sm btn-outline-secondary"
                        @click="goToHistory(props.row)"
                        title="View"
                      >
                        <i class="mdi mdi-eye-outline"></i>
                      </button>
                      <button
                        v-if="permision_role.method_update"
                        type="button"
                        class="btn btn-icon btn-sm btn-outline-primary"
                        @click="openEditModal(props.row)"
                        title="Edit"
                      >
                        <i class="mdi mdi-square-edit-outline"></i>
                      </button>
                      <button
                        v-if="permision_role.method_delete"
                        type="button"
                        class="btn btn-icon btn-sm btn-outline-danger"
                        @click="deleteCustomer(props.row)"
                        title="Delete"
                      >
                        <i class="mdi mdi-trash-can-outline"></i>
                      </button>
                    </div>
                  </span>

                  <!-- Kolom Rental Pro-rate -->
                  <span v-else-if="props.column.field === 'rental_prorate'">
                    <span
                      :class="props.row.rental_prorate === 'Y' ? 'badge bg-soft-success text-success' : 'badge bg-soft-secondary text-secondary'"
                    >
                      {{ props.row.rental_prorate === 'Y' ? "enable" : "disable" }}
                    </span>
                  </span>

                  <span v-else>
                    {{ props.formattedRow[props.column.field] || "-" }}
                  </span>
                </template>
              </vue-good-table>
            </div>
          </div>
        </div>

        <footer-component></footer-component>
      </div>
    </div>

    <!-- Modal Tambah / Edit Company Rental -->
    <modal-proforma-rental-company
      :show-modal="showModal"
      :data="modalData"
      :pi-endpoint="proformaInvoiceEndpoint"
      @closeModal="closeModal"
      @saved="loadItems"
    ></modal-proforma-rental-company>
  </div>
</template>

<script>
import sidebarComponent from "@/components/_partials/_sidebar";
import navbarComponent from "@/components/_partials/_navbar";
import footerComponent from "@/components/_partials/_footer";
import ModalProformaRentalCompany from "@/components/modal/modalProformaRentalCompany";

export default {
  name: "ProformaRentalList",
  components: {
    "sidebar-component": sidebarComponent,
    "navbar-component": navbarComponent,
    "footer-component": footerComponent,
    "modal-proforma-rental-company": ModalProformaRentalCompany,
  },
  data() {
    return {
      isLoading: false,
      showModal: false,
      modalData: null,
      totalRecords: 0,
      permision_role: {
        method_create: true,
        method_read: true,
        method_update: true,
        method_delete: true,
      },
      serverParams: {
        page: 1,
        per_page: 10,
      },
      columnFilters: {
        company_code: "",
        company_name: "",
        sap_code: "",
      },
      rows: [],
    };
  },
  computed: {
    columns() {
      return [
        { label: "Action", field: "actions", sortable: false, filterOptions: { enabled: false } },
        {
          label: "Company Code",
          field: "company_code",
          filterOptions: { enabled: true, placeholder: "" },
        },
        {
          label: "Company Name",
          field: "company_name",
          filterOptions: { enabled: true, placeholder: "" },
        },
        {
          label: "SAP Code",
          field: "sap_code",
          filterOptions: { enabled: true, placeholder: "" },
        },
        {
          label: "Rental Pro-Rate",
          field: "rental_prorate",
          sortable: false,
          filterOptions: { enabled: false },
        },
      ];
    },
    proformaInvoiceEndpoint() {
      return String(this.$settings?.endPointProformaInvoice || "").replace(/\/?$/, "/");
    },
  },
  methods: {
    // ── Navigation ──────────────────────────────────────────────────────────
    goToHistory(row) {
      this.$router.push({
        name: "ProformaRentalHistory",
        params: { customerId: row.proforma_rental_company_id },
        query: {
          company_name: row.company_name,
          company_code: row.company_code,
          sap_code: row.sap_code,
          company_id: row.company_id,
        },
      });
    },

    // ── Modal ────────────────────────────────────────────────────────────────
    openCreateModal() {
      this.modalData = null;
      this.showModal = true;
    },

    async openEditModal(row) {
      this.isLoading = true;
      try {
        const response = await this.$http.get(
          `${this.proformaInvoiceEndpoint}v1/proforma-rental-company/detail/${row.proforma_rental_company_id}`
        );
        const detail = response.data?.datas || response.data?.data || {};
        this.modalData = {
          proforma_rental_company_id: detail.proforma_rental_company_id,
          company_id:    detail.company_id,
          company_name:  detail.company_name,
          company_code:  detail.company_code,
          sap_code:      detail.sap_code,
          rental_prorate: detail.rental_prorate,
        };
        this.showModal = true;
      } catch (error) {
        this.handleRequestError(error, "Gagal load detail data.");
      } finally {
        this.isLoading = false;
      }
    },

    closeModal() {
      this.showModal = false;
      this.modalData = null;
    },

    // ── Delete ────────────────────────────────────────────────────────────────
    async deleteCustomer(row) {
      const result = await this.$swal({
        title: this.$t("areYouSure"),
        text: `Company ${row.company_name} akan dihapus.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
      });

      if (!result.value) return;

      this.isLoading = true;
      try {
        await this.$http.delete(
          `${this.proformaInvoiceEndpoint}v1/proforma-rental-company/delete/${row.proforma_rental_company_id}`
        );
        this.showSuccess("Company berhasil dihapus.");
        await this.loadItems();
      } catch (error) {
        this.handleRequestError(error, "Gagal menghapus company.");
      } finally {
        this.isLoading = false;
      }
    },

    // ── Table ────────────────────────────────────────────────────────────────
    onColumnFilter() {
      this.serverParams.page = 1;
      this.loadItems();
    },
    onPageChange(params) {
      this.serverParams.page = params.currentPage;
      this.loadItems();
    },

    async loadItems() {
      this.isLoading = true;
      const params = {
        page: this.serverParams.page,
        limit: this.serverParams.per_page,
      };

      if (this.columnFilters.company_code) params.company_code = this.columnFilters.company_code;
      if (this.columnFilters.company_name)  params.company_name  = this.columnFilters.company_name;
      if (this.columnFilters.sap_code)      params.sap_code      = this.columnFilters.sap_code;

      try {
        const response = await this.$http.get(
          `${this.proformaInvoiceEndpoint}v1/proforma-rental-company`,
          { params }
        );
        const datas = response.data?.datas || {};
        this.rows = (datas.data || []).map((item) => ({
          proforma_rental_company_id: item.proforma_rental_company_id,
          company_id:     item.company_id,
          company_code:   item.company_code   || "-",
          company_name:   item.company_name   || "-",
          sap_code:       item.sap_code       || "-",
          rental_prorate: item.rental_prorate  || "N",
        }));
        this.totalRecords = datas.total || 0;
      } catch (error) {
        this.rows = [];
        this.totalRecords = 0;
        this.handleRequestError(error, "Gagal load data proforma rental company.");
      } finally {
        this.isLoading = false;
      }
    },

    // ── Permission ───────────────────────────────────────────────────────────
    fetchIt() {
      const userDatas = this.$getUserInfo ? this.$getUserInfo() : null;
      if (!userDatas?.sub?.permision_role) {
        this.loadItems();
        return;
      }

      let rolePermision = null;
      userDatas.sub.permision_role.forEach((permision) => {
        if (
          permision.permission.controller === "ProformaInvoiceNstController" ||
          permision.permission.controller === "ProformaRentalHeaderController"
        ) {
          rolePermision = permision;
        }
      });

      if (rolePermision && !rolePermision.method_read) {
        this.$router.push("/authorized-error");
        return;
      }

      if (rolePermision) {
        this.permision_role = rolePermision;
      }

      this.loadItems();
    },

    // ── Helpers ──────────────────────────────────────────────────────────────
    handleRequestError(error, fallbackMessage) {
      if (error?.response?.status === 500) {
        this.$router.push("/server-error");
        return;
      }
      if (error?.response?.status === 404) {
        return;
      }
      this.showError(
        error?.response?.data?.errors?.message ||
        error?.response?.data?.message ||
        fallbackMessage
      );
    },
    showError(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      Toast.fire({ icon: "error", title: message });
    },
    showSuccess(message) {
      const Toast = this.$swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      Toast.fire({ icon: "success", title: message });
    },
  },
  mounted() {
    this.fetchIt();
  },
};
</script>

<style scoped>
.bg-soft-success {
  background: rgba(25, 135, 84, 0.15);
}
.bg-soft-secondary {
  background: rgba(108, 117, 125, 0.15);
}
</style>
